import { clientConfig, serverConfig } from "app/config";
import { createStore, getJsonFromUrl } from "app/main/functions";
import { mapForeignIsin, getInstrumentUrl } from "app/main/urls";
import dynamic from "next/dynamic";
import React from "react";
import fetch from "isomorphic-unfetch";
import { Store } from "app/main/DataProvider";

import { GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { PageDataProvider } from "app/main/PageDataProvider";
import { getStockIndexConstituentsAPIUrl } from "app/search/urls";
import { getJsonOrRedirect } from "./redirecter";
import { setSentryContext } from "app/sentry/ssr";
import { isValidISIN } from "app/validation/isinValidator";

const instrumentPages = {
    Share: dynamic(() => import("app/shares/ShareDetailPageContainer/ShareDetailPageContainer")),
    ETF: dynamic(() => import("app/etf/ETFDetailPageContainer/ETFDetailPageContainer")),
    Fund: dynamic(() => import("app/funds/FundDetailPageContainer/FundDetailPageContainer")),
    Index: dynamic(() => import("app/stockIndexes/StockIndexDetailPageContainer/StockIndexDetailPageContainer")),
    Commodity: dynamic(() => import("app/commodities/CommodityDetailPageContainer/CommodityDetailPageContainer")),
    ExchangeRate: dynamic(() =>
        import("app/exchangeRates/ExchangeRateDetailPageContainer/ExchangeRateDetailPageContainer")
    )
};

interface Props {
    isin: string;
    type: "Share" | "ETF" | "Fund" | "Index" | "Commodity" | "ExchangeRate";
    store: Store;
}

function InstrumentDetailPage({ isin, type, store }: Props) {
    const PageComponent = instrumentPages[type];

    return (
        <PageDataProvider value={store}>
            <PageComponent isin={isin} />
        </PageDataProvider>
    );
}

export async function getServerSideProps(context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<any>> {
    setSentryContext(context);

    const isin = mapForeignIsin(context.query.isin as string);
    if (!isValidISIN(isin)) {
        return {
            notFound: true
        };
    }

    const requestUrl = context?.req?.url || "";
    const url = getInstrumentUrl(serverConfig.apiURL, isin);
    const res = await fetch(url);

    const json = await getJsonOrRedirect(context, res);

    if (!json) {
        return { props: {} };
    }

    const store = createStore(json, requestUrl, clientConfig);

    if (json.assetClass === "Index") {
        const constituentsURL = getStockIndexConstituentsAPIUrl(serverConfig.apiURL, json.valor);

        store.cache.constituents = {
            [json.valor]: await fetch(constituentsURL).then(response => response.json())
        };

        store.topIndexes = await getJsonFromUrl(`${serverConfig.apiURL}/tiles/top-indexes`);
    }

    return {
        props: {
            isin,
            type: json.assetClass,
            store
        }
    };
}

export default InstrumentDetailPage;
